// src/utils/cache/data-cache.js
// Data/API caching using localStorage (persists across sessions)
// Single source of truth for API response caching
// Automatically invalidates cache when database version changes

import { LOGGING } from '/settings.js';

const CACHE_PREFIX = (window.location.hostname.replace(/\W+/g, '_') || 'mapapp') + '_cache_';
const DB_VERSION_KEY = `${CACHE_PREFIX}_db_version`;
const IMAGES_VERSION_KEY = `${CACHE_PREFIX}_images_version`;
const STORAGE = localStorage; // Use localStorage for persistence across sessions

// Database version cache (in-memory, checked on app init)
let currentDbVersion = null;
let currentImagesVersion = null;
let dbVersionCheckPromise = null;

/**
 * Global fetch queue to prevent duplicate in-flight requests to the same URL.
 * Maps versionedUrl -> Promise
 */
const fetchQueue = new Map();

/**
 * Checks the database version and invalidates cache if it changed.
 * This is called automatically on app initialization.
 * @returns {Promise<string>} The current database version
 */
export async function checkDatabaseVersion() {
    // If already checking, return the same promise
    if (dbVersionCheckPromise) {
        return dbVersionCheckPromise;
    }

    dbVersionCheckPromise = (async () => {
        try {
            // Fetch current database version (direct fetch for initialization)
            // Use cache: 'no-cache' so the browser revalidates with ETag/Last-Modified
            const response = await fetch('/app_core/api/sqlite/get-app-version.php', { cache: 'no-cache' });

            // If server responds 304 Not Modified, reuse stored versions
            if (response.status === 304) {
                const storedDbVersion = STORAGE.getItem(DB_VERSION_KEY) || '1.0.0';
                const storedImagesVersion = STORAGE.getItem(IMAGES_VERSION_KEY) || '1.0.0';
                currentDbVersion = storedDbVersion;
                currentImagesVersion = storedImagesVersion;
                return { dbVersion: storedDbVersion, imagesVersion: storedImagesVersion };
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            const dbVersion = data.version || '1.0.0';
            const imagesVersion = data.images_version || '1.0.0';

            // Get stored versions from localStorage
            const storedDbVersion = STORAGE.getItem(DB_VERSION_KEY);
            const storedImagesVersion = STORAGE.getItem(IMAGES_VERSION_KEY);

            if (storedDbVersion && storedDbVersion !== dbVersion) {
                // Database version changed - clear all caches
                if (LOGGING.ENABLED && LOGGING.CACHE) {
                    console.log(`[Data Cache] Database version changed: ${storedDbVersion} → ${dbVersion}. Clearing all caches.`);
                }
                clearAllCache();
                // Notify runtime caches (in-memory) so they can clear themselves as well
                try {
                    window.dispatchEvent(new CustomEvent('dbVersionChanged', { detail: { oldVersion: storedDbVersion, newVersion: dbVersion } }));
                    if (LOGGING.ENABLED && LOGGING.CACHE) console.log('[Data Cache] Dispatched dbVersionChanged event');
                } catch (e) {
                    if (LOGGING.ENABLED && LOGGING.CACHE) console.warn('[Data Cache] Failed to dispatch dbVersionChanged event:', e);
                }
            } else if (!storedDbVersion && LOGGING.ENABLED && LOGGING.CACHE) {
                console.log(`[Data Cache] First time - storing database version: ${dbVersion}`);
            }

            if (storedImagesVersion && storedImagesVersion !== imagesVersion) {
                // Images version changed - notify for image cache invalidation
                if (LOGGING.ENABLED && LOGGING.CACHE) {
                    console.log(`[Data Cache] Images version changed: ${storedImagesVersion} → ${imagesVersion}. Browser will fetch new images.`);
                }
                // Dispatch event so image utilities know to use new version
                try {
                    window.dispatchEvent(new CustomEvent('imagesVersionChanged', { detail: { oldVersion: storedImagesVersion, newVersion: imagesVersion } }));
                } catch (e) {
                    if (LOGGING.ENABLED && LOGGING.CACHE) console.warn('[Data Cache] Failed to dispatch imagesVersionChanged event:', e);
                }
            } else if (!storedImagesVersion && LOGGING.ENABLED && LOGGING.CACHE) {
                console.log(`[Data Cache] First time - storing images version: ${imagesVersion}`);
            }

            // Store current versions
            STORAGE.setItem(DB_VERSION_KEY, dbVersion);
            STORAGE.setItem(IMAGES_VERSION_KEY, imagesVersion);
            currentDbVersion = dbVersion;
            currentImagesVersion = imagesVersion;

            return { dbVersion, imagesVersion };
        } catch (error) {
            console.error('[Data Cache] Error checking database version:', error);
            // Fallback to stored version or default
            const storedDbVersion = STORAGE.getItem(DB_VERSION_KEY) || '1.0.0';
            const storedImagesVersion = STORAGE.getItem(IMAGES_VERSION_KEY) || '1.0.0';
            currentDbVersion = storedDbVersion;
            currentImagesVersion = storedImagesVersion;
            return { dbVersion: storedDbVersion, imagesVersion: storedImagesVersion };
        }
    })();

    return dbVersionCheckPromise;
}

/**
 * Gets the current database version (from cache or fetches if needed).
 * @returns {Promise<Object>} The current versions {dbVersion, imagesVersion}
 */
export async function getDatabaseVersion() {
    if (currentDbVersion && currentImagesVersion) {
        return { dbVersion: currentDbVersion, imagesVersion: currentImagesVersion };
    }
    return checkDatabaseVersion();
}

/**
 * Gets the current images version synchronously (from cache).
 * Returns default if not yet loaded.
 * @returns {string} The current images version
 */
export function getImagesVersion() {
    return currentImagesVersion || STORAGE.getItem(IMAGES_VERSION_KEY) || '1.0.0';
}

/**
 * A wrapper for the fetch API that introduces a caching layer using localStorage.
 * Cache persists across browser sessions until database version changes.
 *
 * @param {string} url - The URL of the resource to fetch.
 * @param {object} [options={}] - Standard fetch options.
 * @param {number} [durationInMinutes=120] - How long to keep the data in cache (in minutes).
 * @returns {Promise<any>} A promise that resolves to the JSON-parsed data.
 */
export async function fetchWithCache(url, options = {}, durationInMinutes = 43200) {
    // 43200 minutes = 30 days
    // Cache is invalidated when version parameter changes (?v=X.X.X)
    // Ensure database version is checked (non-blocking)
    if (!currentDbVersion) {
        checkDatabaseVersion().catch(err => console.warn('[Data Cache] Version check failed:', err));
    }

    // Get database version for cache key (use stored version if available, otherwise default)
    const dbVersion = currentDbVersion || STORAGE.getItem(DB_VERSION_KEY) || '1.0.0';

    // Automatically append the database version to the URL for cache-busting.
    // This ensures that when the database version changes, a new cache key is used.
    const versionedUrl = url.includes('?')
        ? `${url}&v=${dbVersion}`
        : `${url}?v=${dbVersion}`;
    const cacheKey = `${CACHE_PREFIX}${versionedUrl}`;

    // 1. Try to get data from the cache
    try {
        const cachedItem = STORAGE.getItem(cacheKey);
        if (cachedItem) {
            const { value, expiry } = JSON.parse(cachedItem);

            // Check if the cache item has expired
            if (new Date().getTime() < expiry) {
                if (LOGGING.ENABLED && LOGGING.CACHE) console.log(`[Cache] HIT for: ${versionedUrl}`);
                return Promise.resolve(value);
            } else {
                // Remove expired item
                STORAGE.removeItem(cacheKey);
            }
        }
    } catch (e) {
        console.error("Cache read error:", e);
        // If there's an error, proceed to fetch from the network
    }

    // 2. If not in cache or expired, fetch from the network
    if (LOGGING.ENABLED && LOGGING.CACHE) console.log(`[Cache] MISS for: ${versionedUrl}. Fetching from network.`);

    // DEDUPLICATION: If a request for this versioned URL is already in-flight, await it
    if (fetchQueue.has(versionedUrl)) {
        if (LOGGING.ENABLED && LOGGING.CACHE) console.log(`[Cache] DEDUPLICATING in-flight request for: ${versionedUrl}`);
        return fetchQueue.get(versionedUrl);
    }

    const fetchPromise = (async () => {
        try {
            const response = await fetch(versionedUrl, options);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();

            // 3. Store the newly fetched data in the cache with an expiration date
            try {
                const expirationMs = durationInMinutes * 60 * 1000;
                const record = {
                    value: data,
                    expiry: new Date().getTime() + expirationMs,
                };
                STORAGE.setItem(cacheKey, JSON.stringify(record));
            } catch (e) {
                console.error("Cache write error:", e);
            }
            return data;
        } finally {
            // Always remove from queue when finished
            fetchQueue.delete(versionedUrl);
        }
    })();

    fetchQueue.set(versionedUrl, fetchPromise);
    return fetchPromise;
}

/**
 * Clears a specific item from the cache.
 * @param {string} url - The URL key of the cache item to clear.
 */
export function clearCache(url) {
    const cacheKey = `${CACHE_PREFIX}${url}`;
    STORAGE.removeItem(cacheKey);
}

/**
 * Clears all cached items created by this utility.
 * Note: This does NOT clear the version keys (DB_VERSION_KEY, IMAGES_VERSION_KEY).
 */
export function clearAllCache() {
    Object.keys(STORAGE).forEach(key => {
        if (key.startsWith(CACHE_PREFIX) && key !== DB_VERSION_KEY && key !== IMAGES_VERSION_KEY) {
            STORAGE.removeItem(key);
        }
    });
    if (LOGGING.ENABLED && LOGGING.CACHE) {
        console.log('[Data Cache] Cleared all cache');
    }
}

/**
 * Clears area-specific cached items.
 * This clears cache for endpoints that depend on area (packs, tags, maps, etc.)
 * @param {number} areaId - Area ID to clear cache for (optional, clears all area-specific if not provided)
 */
export function clearAreaCache(areaId = null) {
    const areaSpecificPatterns = [
        'spots-explore-2-packs.php',
        'spots-explore-3-tags.php',
        'get-maps.php'
    ];

    let clearedCount = 0;
    Object.keys(STORAGE).forEach(key => {
        if (key.startsWith(CACHE_PREFIX) && key !== DB_VERSION_KEY) {
            // Check if this cache entry is area-specific
            const isAreaSpecific = areaSpecificPatterns.some(pattern => key.includes(pattern));

            if (isAreaSpecific) {
                // If areaId is provided, only clear if it matches
                if (areaId !== null) {
                    // Extract areaId from cache key (URL contains areaId parameter)
                    const urlMatch = key.match(/areaId=(\d+)|area_id=(\d+)/);
                    if (urlMatch) {
                        const cachedAreaId = parseInt(urlMatch[1] || urlMatch[2], 10);
                        if (cachedAreaId === areaId) {
                            STORAGE.removeItem(key);
                            clearedCount++;
                        }
                    }
                } else {
                    // Clear all area-specific cache
                    STORAGE.removeItem(key);
                    clearedCount++;
                }
            }
        }
    });

    if (LOGGING.ENABLED && LOGGING.CACHE) {
        if (areaId !== null) {
            console.log(`[Data Cache] Cleared cache for area ${areaId} (${clearedCount} items)`);
        } else {
            console.log(`[Data Cache] Cleared all area-specific cache (${clearedCount} items)`);
        }
    }
}
